import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { tenancyApi } from '@/services/tenancy.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, EmptyState, Spinner, StatusBadge } from '@/components/ui';

/**
 * Every appearance theme this tenant has — the seeded default (cloneable,
 * never editable or deletable in place, "cloneable but not editable in
 * place" is the same rule a seeded system role already follows) plus
 * whatever custom themes it composed from it.
 */
export function AppearancePage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const canManage = can('tenancy.tenant.admin');

  const themes = useQuery({
    queryKey: ['tenancy', 'themes'],
    queryFn: () => tenancyApi.listThemes(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenancy', 'themes'] });

  const activate = useMutation({
    mutationFn: (id: string) => tenancyApi.activateTheme(id),
    onSuccess: () => {
      void invalidate();
      void queryClient.invalidateQueries({ queryKey: ['tenancy', 'themes', 'active'] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => tenancyApi.deleteTheme(id),
    onSuccess: invalidate,
  });

  const themeList = themes.data ?? [];
  const mutationError = activate.error ?? remove.error;

  return (
    <div className="page">
      <header className="page-header">
        <h1 className="page-title">{t('administration.appearance.title')}</h1>
        {canManage && (
          <Link className="btn" data-variant="primary" to="/administration/appearance/new">
            {t('administration.appearance.new')}
          </Link>
        )}
      </header>

      {mutationError && (
        <Alert tone="danger">
          {mutationError instanceof ApiError
            ? t(`errors.${mutationError.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      {themes.isPending && <Spinner label={t('common.states.loading')} />}
      {themes.isSuccess && themeList.length === 0 && (
        <EmptyState title={t('administration.appearance.empty')} />
      )}

      {themeList.length > 0 && (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('administration.appearance.name')}</th>
                <th scope="col">{t('administration.appearance.status')}</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {themeList.map((theme) => (
                <tr key={theme.id}>
                  <td>{theme.name}</td>
                  <td>
                    {theme.isDefault && (
                      <StatusBadge
                        status="TRIALING"
                        label={t('administration.appearance.default')}
                      />
                    )}{' '}
                    {theme.isActive && (
                      <StatusBadge status="ACTIVE" label={t('administration.appearance.active')} />
                    )}
                  </td>
                  <td>
                    {!theme.isDefault && (
                      <Link className="btn" to={`/administration/appearance/${theme.id}`}>
                        {t('common.actions.edit')}
                      </Link>
                    )}{' '}
                    <Link
                      className="btn"
                      to={`/administration/appearance/new?cloneFrom=${theme.id}`}
                    >
                      {t('administration.appearance.clone')}
                    </Link>{' '}
                    {canManage && !theme.isActive && (
                      <Button
                        variant="secondary"
                        loading={activate.isPending && activate.variables === theme.id}
                        onClick={() => activate.mutate(theme.id)}
                      >
                        {t('administration.appearance.activate')}
                      </Button>
                    )}{' '}
                    {canManage && !theme.isDefault && !theme.isActive && (
                      <Button
                        variant="ghost"
                        loading={remove.isPending && remove.variables === theme.id}
                        onClick={() => {
                          if (window.confirm(t('administration.appearance.confirmDelete'))) {
                            remove.mutate(theme.id);
                          }
                        }}
                      >
                        {t('administration.appearance.delete')}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
